import * as CSS from 'csstype'

import {AccentThemeType, disabledAlpha} from './styles/theme'
import {BreakStyle, defineStyles, useThemedStyles} from './styles/style'
import React, {CSSProperties} from 'react'

import {hexToRgb} from 'notability-services-common-universal'
import {useMouseover} from './hooks'

export const appearances = ['filled', 'stroked', 'tinted', 'plain'] as const
export type Appearance = (typeof appearances)[number]
type FontWeight = 'regular' | 'bold'
type State = 'active' | 'disabled' | 'hovered' | 'pressed'
type Shape = 'default' | 'rounded'
type Size = 'xSmall' | 'small' | 'medium' | 'large'

export interface ActionButtonProps extends Pick<React.ComponentPropsWithoutRef<'div'>, 'key' | 'className' | 'style'> {
  onClick?: React.MouseEventHandler

  /** Create an icon correctly themed for the current view. */
  iconCreator?: (color: CSS.Property.Color) => React.ReactElement | undefined

  /** Determines whether the provided icon leads or trails the text */
  iconJustify?: 'leading' | 'trailing'

  /** UI text to display */
  text?: string

  appearance: Appearance

  color?: AccentThemeType

  buttonShape?: Shape

  textWeight?: FontWeight

  style?: CSSProperties

  size: Size

  /** True to render it grayed out with no pointer cursor and disable the click action */
  disabled?: boolean

  /** If it's a link, provide the href. */
  href?: string

  /** True if the href should open in a new window */
  hrefTargetBlank?: boolean

  /** Changes margins based on orientation compared to other buttons. Default horizontal. */
  orientation?: 'horizontal' | 'vertical'

  formSubmit?: boolean

  asButton?: boolean
}

/**
 * A button that changes its colors when you hover.  Can take an icon, some text, or both.
 */
export const ActionButton = React.forwardRef(function ActionButton(
  {
    iconCreator,
    text,
    disabled,
    onClick,
    href,
    hrefTargetBlank,
    appearance,
    size,
    formSubmit,
    asButton,
    iconJustify = 'leading',
    buttonShape = 'default',
    color = 'accent',
    textWeight = 'bold',
    style = {},
    ...otherProps
  }: ActionButtonProps,
  ref: React.Ref<any>
) {
  const s = useThemedStyles(styles)
  const [hovered, mouseOverProps] = useMouseover()
  const [pressed, setMouseDown] = React.useState(false)
  const iconOnly = !text && !!iconCreator
  const state: State = disabled ? 'disabled' : pressed ? 'pressed' : hovered ? 'hovered' : 'active'

  const finalStyle = React.useMemo<React.CSSProperties>(() => {
    const returnStyle = s.buttonStyle(size, buttonShape, appearance, color, state, textWeight, iconOnly)

    return {...returnStyle, ...style}
  }, [size, buttonShape, appearance, s, state, style, iconOnly, color, textWeight])

  const icon = iconCreator && iconCreator(finalStyle.color!)
  const textComponent = text && <p style={{alignSelf: 'center'}}>{text}</p>

  asButton = asButton || formSubmit
  const ButtonTag = asButton ? 'button' : 'div'

  const button = (
    <ButtonTag
      className="action-button-container"
      ref={ref}
      type={formSubmit ? 'submit' : undefined}
      onMouseDown={
        asButton
          ? undefined
          : () => {
              setMouseDown(true)
            }
      }
      onClick={formSubmit || disabled ? undefined : onClick}
      onMouseUp={
        asButton
          ? undefined
          : () => {
              setMouseDown(false)
            }
      }
      onMouseOut={
        asButton
          ? undefined
          : () => {
              setMouseDown(false)
            }
      }
      style={finalStyle}
      {...mouseOverProps}
      {...otherProps}
    >
      {iconJustify === 'leading' ? icon : textComponent}
      {iconJustify === 'leading' ? textComponent : icon}
    </ButtonTag>
  )

  return href ? (
    <a
      className="no-underline"
      href={href}
      {...(hrefTargetBlank ? {target: '_blank', rel: 'noopener noreferrer'} : {})}
    >
      {button}
    </a>
  ) : (
    button
  )
})

const Dimension = {
  large: '58px',
  medium: '48px',
  small: '40px',
  xSmall: '32px'
}

const styles = defineStyles((t, font) => {
  const sizeClasses: Record<Size, BreakStyle> = {
    large: {
      height: Dimension.large,
      minWidth: Dimension.large,
      padding: '16px 24px'
    },
    medium: {
      height: Dimension.medium,
      minWidth: Dimension.medium,
      padding: '12px 16px'
    },
    small: {
      height: Dimension.small,
      minWidth: Dimension.small,
      padding: '8px 16px'
    },
    xSmall: {
      height: Dimension.xSmall,
      minWidth: Dimension.xSmall,
      padding: '4px 16px'
    }
  }
  const iconOnlySizeClasses: Record<Size, BreakStyle> = {
    large: {
      ...sizeClasses.large,
      padding: '16px'
    },
    medium: {
      ...sizeClasses.medium,
      padding: '12px'
    },
    small: {
      ...sizeClasses.small,
      maxWidth: Dimension.small,
      padding: '10px'
    },
    xSmall: {
      ...sizeClasses.xSmall,
      maxWidth: Dimension.xSmall,
      padding: '4px'
    }
  }
  const shapes: Record<Shape, BreakStyle> = {
    rounded: {
      borderRadius: '128px'
    },
    default: {
      borderRadius: '8px'
    }
  }

  //TODO: when the themes are updated make sure to update the colors in the appearances obj
  const appearances = (color: AccentThemeType): Record<Appearance, Record<State, BreakStyle>> => ({
    filled: {
      active: {
        color: t.core.onAccent,
        backgroundColor: t[color].base
      },
      disabled: {
        color: hexToRgb(t.core.onAccent, disabledAlpha),
        backgroundColor: hexToRgb(t[color].base, disabledAlpha)
      },
      hovered: {
        color: t.core.onAccent,
        backgroundColor: t[color].hover
      },
      pressed: {
        color: t.core.onAccent,
        backgroundColor: t[color].press
      }
    },
    stroked: {
      active: {
        color: t[color].base,
        border: `1.25px solid ${t[color].base}`
      },
      disabled: {
        border: `1.25px solid ${hexToRgb(t[color].base, disabledAlpha)}`,
        color: hexToRgb(t[color].base, disabledAlpha)
      },
      hovered: {
        color: t[color].hover,
        backgroundColor: t[color].containerHover,
        border: `1.25px solid ${t[color].hover}`
      },
      pressed: {
        color: t[color].press,
        backgroundColor: t[color].containerPress,
        border: `1.25px solid ${t[color].press}`
      }
    },
    plain: {
      active: {
        color: t[color].base
      },
      disabled: {
        color: hexToRgb(t[color].base, disabledAlpha)
      },
      hovered: {
        color: t[color].hover,
        backgroundColor: t[color].containerHover
      },
      pressed: {
        color: t[color].press,
        backgroundColor: t[color].containerPress
      }
    },
    tinted: {
      active: {
        backgroundColor: t[color].container,
        color: t[color].base
      },
      disabled: {
        backgroundColor: hexToRgb(t[color].container, disabledAlpha),
        color: hexToRgb(t[color].base, disabledAlpha)
      },
      hovered: {
        backgroundColor: t[color].containerHover,
        color: t[color].hover
      },
      pressed: {
        backgroundColor: t[color].containerPress,
        color: t[color].press
      }
    }
  })

  const colorTransition: BreakStyle = {
    transitionProperty: 'background-color, color, border-color',
    transitionDuration: '0.25s',
    transitionTimingFunction: 'ease-out'
  }
  return {
    buttonStyle: (
      size: Size,
      shape: Shape,
      appearance: Appearance,
      color: AccentThemeType,
      state: State,
      fontWeight: FontWeight,
      iconOnly: boolean
    ) => {
      const sizeClass = iconOnly ? iconOnlySizeClasses[size] : sizeClasses[size]
      const a = appearances(color)
      const fontStyle = fontWeight === 'bold' ? font.bodyBold : font.body
      return {
        ...colorTransition,
        ...a[appearance][state],
        ...sizeClass,
        ...shapes[shape],
        ...fontStyle,
        display: 'flex',
        flexDirection: 'row',
        flexShrink: 0,
        justifyContent: 'center',
        alignContent: 'center',
        gap: '8px',
        userSelect: 'none',
        cursor: state === 'disabled' ? 'default' : 'pointer',
        WebkitUserSelect: 'none',
        WebkitTouchCallout: 'none',
        whiteSpace: 'nowrap'
      }
    }
  }
})
